import { connect, ConnectedProps, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "store";
import GovernmentalEntity from "./GovernmentalEntity";
import { actions as notificationActions } from "reducers/notification";
import { actions as governmentalEntityActions } from "reducers/governmentalEntity";
import { useTranslation } from "react-i18next";
import { useState, useContext, useEffect, useRef } from "react";
import "./i18n";
import { NotificationStateType } from "reducers/types/notification";
import { ToastContext } from "@pickit/pickit-components";
import { history } from "reducers";
import { dirtyAlert } from "utils/alerts";

const GovernmentalEntityContainer = ({
  getVersions,
  fileStatus,
  versions,
  loading,
  showNotification,
  uploadVersion,
  setFileStatus,
  clearFileStatus,
}: ConnectorProps) => {
  const { t } = useTranslation("governmentalEntity");
  const { generateToast } = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const [currentFile, setCurrentFile] = useState<{
    url: string;
    name?: string;
    file?: File;
  } | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const lastErrorMessageRef = useRef<string | null>(null);

  useEffect(() => {
    getVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      setCurrentFile({ url, name: file.name, file });
    } catch (error) {
      generateToast({ body: t("errorMessage"), type: "error" });
    }
  };

  const handleUpdateClick = () => {
    const fileName =
      currentFile?.file?.name || currentFile?.name || "entidad-gubernamental.pdf";
    showNotification({
      title: t("confirmUpdateTitle"),
      body: t("confirmUpdateMessage", { fileName }),
      type: "warning",
      onConfirm: handleUpdate,
      onConfirmLabel: "global:confirm",
    });
  };

  const handleUpdate = () => {
    if (!currentFile || !currentFile.file) {
      return;
    }
    dispatch(
      governmentalEntityActions.setFileStatus({ loading: true, error: false })
    );
    uploadVersion(currentFile.file);
  };

  const goToPreviousFile = () => {
    setCurrentFile(null);
    setFileStatus({
      error: false,
      loading: false,
      message: "",
      justUploaded: false,
    });
  };

  const downloadVersion = async (
    versionId: string,
    url: string,
    fileName: string
  ) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const downloadFileName =
        fileName && fileName !== "file.pdf" && fileName.trim() !== ""
          ? fileName
          : `entidad-gubernamental-v${versionId}.pdf`;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      generateToast({
        body: t("errorDownload") || "Error al descargar el archivo",
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (fileStatus.justUploaded) {
      generateToast({ body: t("successMessage"), type: "confirmation" });
      setCurrentFile(null);
      setFileInputKey((prev) => prev + 1);
      const timeoutId = setTimeout(() => {
        clearFileStatus();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStatus.justUploaded]);

  useEffect(() => {
    if (fileStatus.error && fileStatus.message) {
      const currentMessage = fileStatus.message;
      if (lastErrorMessageRef.current !== currentMessage) {
        lastErrorMessageRef.current = currentMessage;
        generateToast({
          body: currentMessage || t("errorMessage"),
          type: "error",
        });
      }
      setTimeout(() => {
        clearFileStatus();
        lastErrorMessageRef.current = null;
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStatus.error, fileStatus.message]);

  useEffect(() => {
    if (!currentFile) {
      return;
    }

    let pendingLocation: any = null;

    const unblock = history.block((location, _action) => {
      if (currentFile) {
        pendingLocation = location;
        const alert = dirtyAlert({
          onCancel: () => {
            goToPreviousFile();
            unblock();
            if (pendingLocation) {
              history.push(
                pendingLocation.pathname + (pendingLocation.search || "")
              );
            }
          },
          cancelLabel: t("global:notSave"),
          scroll: false,
        });

        showNotification({
          title: alert.title || "",
          body: alert.body || "",
          type: alert.level || "warning",
          onConfirm: alert.onConfirm,
          onCancel: alert.onCancel,
          onConfirmLabel: alert.onConfirmLabel,
          onCancelLabel: alert.onCancelLabel,
        });
        return false;
      }
      return undefined;
    });

    return () => {
      unblock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFile]);

  return (
    <GovernmentalEntity
      testId="governmental-entity"
      currentFile={currentFile}
      fileStatus={fileStatus}
      versions={versions}
      loading={loading}
      fileHandler={handleFileUpload}
      fileInputKey={fileInputKey}
      goToPreviousFile={goToPreviousFile}
      downloadVersion={downloadVersion}
      onUpdate={handleUpdateClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  versions: state.governmentalEntity?.versions || [],
  loading: state.governmentalEntity?.loading || false,
  error: state.governmentalEntity?.error || null,
  fileStatus: state.governmentalEntity?.fileStatus || {
    loading: false,
    error: false,
    message: "",
    justUploaded: false,
  },
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  showNotification: (notification: {
    title: string;
    body?: string;
    type: "success" | "error" | "warning" | "info";
    onConfirm?: Function;
    onCancel?: Function;
    onConfirmLabel?: string;
    onCancelLabel?: string;
  }) => {
    dispatch(
      notificationActions.showNotification({
        title: notification.title,
        level: notification.type as NotificationStateType["level"],
        body: notification.body || "",
        onConfirmLabel: notification.onConfirmLabel || "global:confirm",
        onCancelLabel: notification.onCancelLabel || "global:cancel",
        onConfirm: notification.onConfirm,
        onCancel: notification.onCancel,
      })
    );
  },
  getVersions: () => {
    dispatch(governmentalEntityActions.getVersions({}));
  },
  uploadVersion: (file: File) => {
    dispatch(governmentalEntityActions.uploadVersion({ file }));
  },
  clearFileStatus: () => {
    dispatch(governmentalEntityActions.clearFileStatus());
  },
  setFileStatus: (fileStatus: {
    loading: boolean;
    error: boolean;
    message?: string;
    justUploaded?: boolean;
  }) => {
    dispatch(governmentalEntityActions.setFileStatus(fileStatus));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(GovernmentalEntityContainer);
